import { Location, Position } from './types';

const DISTANCE = 3;
const VIEWPORT_PADDING = 4;
// Keeps the arrow clear of the tooltip's rounded corners.
const ARROW_EDGE_PADDING = 12;

export const getTooltipLocation = (
  position: Position,
  tooltip: DOMRect,
  target: DOMRect
): Location => {
  const halfTooltipWidth = tooltip.width / 2;
  const halfTooltipHeight = tooltip.height / 2;
  const halfTargetWidth = target.width / 2;
  const halfTargetHeight = target.height / 2;

  if (position === 'top') {
    const top = target.y - tooltip.height - DISTANCE;
    const left = target.x - halfTooltipWidth + halfTargetWidth;
    return { top, left };
  }

  if (position === 'right') {
    const top = target.y - halfTooltipHeight + halfTargetHeight;
    const left = target.x + target.width;
    return { top, left };
  }

  if (position === 'left') {
    const top = target.y - halfTooltipHeight + halfTargetHeight;
    const left = target.x - tooltip.width;
    return { top, left };
  }

  // position === 'bottom'
  const top = target.y + target.height + DISTANCE;
  const left = target.x - halfTooltipWidth + halfTargetWidth;
  return { top, left };
};

export const constrainTooltipToViewport = (
  position: Position,
  tooltip: DOMRect,
  target: DOMRect
): Location => {
  // clientWidth/Height exclude a visible scrollbar, so the tooltip can't end up underneath it.
  const windowWidth = document.documentElement.clientWidth;
  const windowHeight = document.documentElement.clientHeight;

  const location = getTooltipLocation(position, tooltip, target);

  const top = Math.min(
    Math.max(location.top, VIEWPORT_PADDING),
    windowHeight - tooltip.height - VIEWPORT_PADDING
  );
  const left = Math.min(
    Math.max(location.left, VIEWPORT_PADDING),
    windowWidth - tooltip.width - VIEWPORT_PADDING
  );

  return { top, left };
};

/**
 * When a tooltip is pushed back inside the viewport its box is no longer
 * centered on the target, so the arrow (centered on the box) stops pointing at
 * it. Returns the transform that slides the arrow along the box edge to line
 * it up again.
 */
export const getArrowTransform = (
  position: Position,
  tooltip: DOMRect,
  target: DOMRect
): string | undefined => {
  const isHorizontalEdge = position === 'top' || position === 'bottom';

  const targetCenter = isHorizontalEdge
    ? target.x + target.width / 2
    : target.y + target.height / 2;
  const tooltipCenter = isHorizontalEdge
    ? tooltip.x + tooltip.width / 2
    : tooltip.y + tooltip.height / 2;
  const tooltipSpan = isHorizontalEdge ? tooltip.width : tooltip.height;

  const limit = Math.max(0, tooltipSpan / 2 - ARROW_EDGE_PADDING);
  const shift = Math.round(targetCenter - tooltipCenter);
  const clamped = Math.max(-limit, Math.min(limit, shift));

  if (!clamped) {
    return undefined;
  }

  return isHorizontalEdge
    ? `translateX(${clamped}px)`
    : `translateY(${clamped}px)`;
};
